import * as uuid from "uuid";
import { Card, FormControl, MenuItem, Select, Typography } from "@mui/material";
import { classes } from "common/theme/theme";
import { couplerTypeToLocaleString } from "common/utils/enumTranslations";
import { editingSupported, RigDetailsFormState } from "./FormState";
import {
  friendlyScvOutputName,
  ImplementConfiguration,
} from "portal/utils/atkEquipment";
import { LibraryCardDetailRow } from "portal/components/atk/LibraryCardDetailRow";
import { ScvInput, ScvOutput } from "protos/atk/autotractor";
import { t } from "i18next";
import { typedPath } from "typed-path";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import React, { useState } from "react";

const S = typedPath<RigDetailsFormState>();

type ImplementAndInput = { implement: ImplementConfiguration; input: ScvInput };

type RigConnectionsSelection =
  | { scvInput: ImplementAndInput | undefined; scvOutput?: undefined }
  | { scvInput?: undefined; scvOutput: ScvOutput | undefined };

export const ScvInputListItem: React.FC<{
  implement: ImplementConfiguration;
  input: ScvInput;
  connectedOutput: ScvOutput | undefined;
  selection: RigConnectionsSelection;
  setSelection: (selection: RigConnectionsSelection) => void;
}> = ({ implement, input, connectedOutput, selection, setSelection }) => {
  const { t } = useTranslation();

  return (
    <li className="list-none flex flex-row w-full cursor-pointer">
      <div
        role="button"
        className={classes("flex flex-row w-full py-2 px-4", {
          "bg-darken-100 dark:bg-lighten-200":
            selection.scvInput?.input === input,
        })}
        onClick={() => setSelection({ scvInput: { implement, input } })}
        tabIndex={0}
      >
        <Typography className="align-middle h-full">
          {input.function}
        </Typography>
        {connectedOutput ? (
          <Typography className="flex-1 text-right h-full">
            {t("views.autotractor.rigDetails.connections.connectedTo")}{" "}
            {friendlyScvOutputName(t, connectedOutput)}
          </Typography>
        ) : (
          <Typography className="text-warning font-bold flex-1 text-right h-full">
            {t("views.autotractor.rigDetails.connections.noConnections")}
          </Typography>
        )}
      </div>
    </li>
  );
};

export const ScvInputDetailView: React.FC<
  ImplementAndInput & { availableOutputs: ScvOutput[] }
> = ({ input, availableOutputs, implement }) => {
  const { values, setFieldValue, setFieldTouched } =
    useFormikContext<RigDetailsFormState>();

  const [error, setError] = useState(false);
  const editingUnsupportedVersion = !editingSupported(values);

  return (
    <Card className="p-4 flex flex-col gap-4 border-2 border-solid rounded-lg">
      <Typography className="text-xl">
        {t("views.autotractor.rigDetails.connections.selectedSCVInput")}{" "}
        {input.function}
      </Typography>
      <LibraryCardDetailRow label="Implement" value={<>{implement.name}</>} />
      <LibraryCardDetailRow
        label="Hydraulic coupler"
        value={<>{couplerTypeToLocaleString(t, input.coupler)}</>}
      />
      <FormControl
        error={error}
        fullWidth
        className="border-0 border-t border-solid"
      >
        <Typography className="py-2">
          {t("views.autotractor.rigDetails.connections.connectedTo")}
        </Typography>
        <Select<string>
          displayEmpty
          disabled={editingUnsupportedVersion}
          onChange={(e) => {
            const path = S.scvConnectionsByInput[input.id]!.$path;
            setFieldTouched(path, true);

            const hasDuplicate = implement.scvInputs?.inputs.some(
              (otherInput) =>
                otherInput !== input &&
                values.scvConnectionsByInput[otherInput.id]
                  ?.tractorScvOutputId === e.target.value
            );
            if (hasDuplicate) {
              setError(true);
              return;
            } else {
              setError(false);
            }

            if (e.target.value === "") {
              setFieldValue(path, undefined);
            } else {
              setFieldValue(path, {
                id: uuid.v4(),
                implementScvInputId: input.id,
                tractorScvOutputId: e.target.value,
              });
            }
          }}
          value={
            values.scvConnectionsByInput[input.id]?.tractorScvOutputId ?? ""
          }
        >
          <MenuItem value={""}>{t("utils.descriptors.none")}</MenuItem>
          {availableOutputs.map((output) => (
            <MenuItem key={output.id} value={output.id}>
              {friendlyScvOutputName(t, output)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Card>
  );
};
